import {
  reactive,
  ref,
  Ref,
  shallowReactive,
  ShallowReactive,
  UnwrapNestedRefs,
  watch,
} from 'vue'
import { ColumnDefByColName } from '../../../common/$models/ModelDef'
import { CanceledError, CancellablePromise } from '../../../common/utils'
import { ModelNameSpecifiedClassBase } from '../../ModelServices/ModelNameSpecifiedClassBase'
import { DataListQuery } from './DataListQuery'
import { FilterControlsService } from './FilterControlsService'

type QueryChangeCallbackFunction = (ins: ComposableDataListService) => void

/**
 * # ComposableDataListService
 *
 * CORE Framework におけるデータ一覧表示機能の中心的な役割を担うサービスです。
 * データの取得、フィルタリング、ソート、ページネーション、検索、表示項目の管理など、
 * データリスト表示に関するあらゆる状態とロジックをカプセル化します。
 *
 * 主に `<ComposableDataList>` コンポーネント (またはそのラッパーコンポーネント) の内部でインスタンス化され、
 * `provide` されることで、配下の様々なUIコンポーネントから `inject` して利用されます。
 * これにより、データリストの状態を一元的に管理し、コンポーネント間の連携を容易にします。
 *
 * ## 主な機能
 * - モデル (通常モデルまたはVirtualモデル) からのデータ取得と表示
 * - フィルタリング条件の管理と適用 (固定フィルタ、UIからの動的フィルタ)
 * - ソート条件の管理と適用
 * - ページネーションの状態管理
 * - キーワード検索
 * - 表示件数の制御
 * - Excelエクスポート機能の呼び出し
 * - 一括操作のための選択状態管理
 * - 表示カラムの動的な管理 (ユーザ設定に基づくなど)
 * - フックポイント (`resolveHookNamePrefix` 経由) を利用したカスタマイズ
 *
 * ## `inject` を利用した基本的な使い方
 *
 * `ComposableDataListService` は、通常、親コンポーネント (例: `ComposableDataList`) から `provide` されます。
 * 子コンポーネントでは `inject` を使用してこのサービスのインスタンスにアクセスできます。
 *
 * ### 例1: サービスインスタンスの取得
 * ```vue
 * <script lang="ts">
 * import { defineComponent, inject } from 'vue';
 * import { ComposableDataListService } from 'path/to/ComposableDataListService'; // 実際のパスに置き換えてください
 *
 * export default defineComponent({
 *   setup() {
 *     // 'ComposableDataListServiceInstance' は通常 provide される際のキー名です
 *     const composableDataListService = inject<ComposableDataListService>('ComposableDataListServiceInstance');
 *
 *     if (!composableDataListService) {
 *       // サービスが inject できなかった場合のフォールバック処理
 *       console.error('ComposableDataListService could not be injected.');
 *       // 必要に応じてエラーハンドリングやデフォルトの動作を定義
 *     }
 *
 *     return {
 *       service: composableDataListService,
 *     };
 *   },
 * });
 * </script>
 * ```
 *
 * ### 例2: リアクティブなデータの利用 (items, isLoading など)
 * ```vue
 * <template>
 *   <div v-if="service?.isLoading.value">Loading...</div>
 *   <ul v-else>
 *     <li v-for="item in service?.items" :key="item.id">{{ item.name }}</li>
 *   </ul>
 *   <p>Total items: {{ service?.filter_count }}</p>
 * </template>
 *
 * <script lang="ts">
 * // ... (上記と同様の setup)
 * </script>
 * ```
 *
 * ### 例3: メソッドの呼び出し (ページネーション変更、検索実行など)
 * ```vue
 * <template>
 *   <button @click="service?.query.setPage(service.query.page + 1)">Next Page</button>
 *   <input type="text" v-model="searchTerm" @input="onSearchInput" />
 * </template>
 *
 * <script lang="ts">
 * import { defineComponent, inject, ref } from 'vue';
 * import { ComposableDataListService } from 'path/to/ComposableDataListService';
 *
 * export default defineComponent({
 *   setup() {
 *     const service = inject<ComposableDataListService>('ComposableDataListServiceInstance');
 *     const searchTerm = ref('');
 *
 *     const onSearchInput = () => {
 *       if (service) {
 *         service.query.search = searchTerm.value; // これにより自動的に watch が発火し find() が呼ばれる (disableFindDataOnQueryChange = false の場合)
 *         // 手動で検索をトリガーしたい場合:
 *         // service.find();
 *       }
 *     };
 *
 *     return { service, searchTerm, onSearchInput };
 *   },
 * });
 * </script>
 * ```
 *
 * ### 例4: 複数の関連サービスとの連携
 * `ComposableDataListService` は、内部で `DataListQuery`, `DataListDataItems`, `FilterControlsService` を保持・利用します。
 * これらのサービスも `provide`/`inject` パターンで利用可能です。
 *
 * - **`DataListDisplayServiceInstance`**: 表示列のカスタマイズや一括選択状態などを管理します。
 *   `<DataListDisplayContainer>` コンポーネントから provide されます。
 * - **`FilterControlsServiceInstance`**: 高度なフィルタリングUIの状態を管理します。
 *   `<ComposableDataListSearchWrapper>` コンポーネント (または直接 `<FilterResultDisplayContainer>`) から provide されます。
 *
 * ```vue
 * <!-- 親コンポーネントのテンプレート例 -->
 * <ComposableDataList modelName="myModel">
 *   <template #default="{ ComposableDataListServiceInstance }">
 *     <!-- 検索UI -->
 *     <ComposableDataListSearchWrapper>
 *       <DataListKeywordSearch />
 *       <FilterResultDisplayContainer />
 *     </ComposableDataListSearchWrapper>
 *
 *     <!-- データ表示UI -->
 *     <DataListDisplayContainer>
 *       <DataListDisplayTable />
 *     </DataListDisplayContainer>
 *
 *     <!-- ページネーションUI -->
 *     <DataListPagination />
 *   </template>
 * </ComposableDataList>
 * ```
 *
 * ## Constructor Props (主なもの)
 * `ComposableDataListService` はコンストラクタで様々なオプションを受け取ります。
 * これらは通常 `<ComposableDataList>` コンポーネントの props として渡されます。
 *
 * @param {string} modelName - 表示するデータのモデル名 (必須)。
 * @param {string} [virtualModelName] - 使用するVirtualモデル名。指定すると、ベースモデルの挙動を上書きします。
 * @param {object | string} [filters] - 初期状態で適用される固定フィルタ。JSON文字列またはオブジェクト形式。
 *                                     例: `{ status: { _eq: 'active' } }`
 * @param {Function | string} [itemClickEvent] - データ行クリック時のカスタムイベントハンドラ。
 *                                              文字列の場合はグローバル関数名として扱われます。
 * @param {string} [searchConditionSaveKey] - 検索条件をLocalStorageに保存する際のキー。
 * @param {string} [resolveHookNamePrefix] - このサービスインスタンスに関連するAppHookのプレフィックス。
 *                                          UIの各パーツのカスタマイズに利用されます。
 *                                          例: `myCustomList` を指定すると、`myCustomList.headerAppend` などのフックが利用可能。
 * @param {boolean} [enableSort=true] - ソート機能の有効/無効。
 * @param {boolean} [enableKeywordSearch=true] - キーワード検索機能の有効/無効。
 * @param {boolean} [enableLimitControl=true] - 表示件数制御機能の有効/無効。
 * @param {string} [limitLabel='表示件数'] - 表示件数制御のラベル。
 * @param {number} [defaultLimit=100] - デフォルトの表示件数。
 * @param {string[]} [filterableColNames] - UIのフィルター選択肢として表示するカラム名の配列。指定しない場合はモデル定義から自動生成。
 * @param {boolean} [enableHeader=true] - ヘッダー領域（タイトルや追加ボタンなど）の表示/非表示。
 * @param {boolean} [enableControls=true] - コントロール領域（検索、フィルタ、ソートなど）の表示/非表示。
 * @param {string} [wrapperClass='card'] - データリスト全体のラッパー要素に適用されるCSSクラス。
 * @param {boolean} [enablePagination=true] - ページネーション機能の有効/無効。
 * @param {string[]} [listColNames] - テーブル表示などで、実際に表示するカラム名の配列。
 *                                   `DataListDisplayService` でさらに詳細な制御が可能です。
 * @param {boolean} [enableExcelExportButton] - Excelエクスポートボタンの表示/非表示。
 * @param {boolean} [enableAddNewRecordButton=false] - 新規追加ボタンの表示/非表示。
 * @param {boolean} [enableFilter=true] - フィルター機能全体の有効/無効。
 * @param {object} [queryFilters] - (非推奨、`filters`を使用) URLクエリパラメータから受け取る初期フィルタ。
 * @param {boolean} [urlQueryFilterChangeable=false] - URLクエリのフィルタをUIから変更可能にするか。
 * @param {string} [headerTitle] - ヘッダーに表示するカスタムタイトル。指定しない場合はモデルのラベル名などから自動生成。
 * @param {number} [initialPage=1] - 初期表示ページ番号。
 *
 * @property {boolean} initialized - サービスが初期化されたかどうかを示すフラグ。
 * @property {Ref<boolean>} _isLoading - (private) データロード中かどうかを示すリアクティブなフラグ。`isLoading`ゲッター経由でアクセス。
 * @property {Record<string, any>[]} bulkControlSelectedItems - 一括操作対象として選択されたアイテムの配列。
 * @property {number} lastCheckedCheckboxItemIndex - Shiftキーを使った範囲選択のための、最後にチェックされたチェックボックスのインデックス。
 * @property {boolean} disableFindDataOnLoad - 初期ロード時に自動で`find()`メソッドを呼び出すのを無効にするか。
 * @property {boolean} disableFindDataOnQueryChange - `query`オブジェクトの変更時に自動で`find()`メソッドを呼び出すのを無効にするか。
 * @property {boolean} disableRefreshDataOnDataEdited - 関連データの編集 (保存/削除) 時に自動でリストを再読み込みするのを無効にするか。
 * @property {UnwrapNestedRefs<DataListQuery>} query - データ取得クエリの状態を管理する `DataListQuery` のインスタンス。リアクティブ。
 * @property {ShallowReactive<DataListDataItems>} dataList - 取得したデータとメタ情報 (総件数など) を保持する `DataListDataItems` のインスタンス。ShallowReactive。
 * @property {Record<string, QueryChangeCallbackFunction>} queryChangeCallbackFunctions - `query` の変更を監視し、追加のロジックを実行するためのコールバック関数群。
 * @property {Ref<number>} lastQueryChangedAt - `query` が最後に変更されたタイムスタンプ。
 * @property {Ref<number>} lastDataFetchedAt - データが最後にフェッチされたタイムスタンプ。
 * @property {FilterControlsService} FilterControlsServiceInstance - 高度なフィルターUIの状態管理を行うサービスインスタンス。
 * @property {() => any} wrapperVueComponentReferenceFunction - (internal) このサービスを保持する親Vueコンポーネントへの参照を返す関数。
 */
export class ComposableDataListService extends ModelNameSpecifiedClassBase {
  public initialized = false
  private _isLoading = reactive({ value: false })
  public bulkControlSelectedItems: Record<string, any>[] = []
  public lastCheckedCheckboxItemIndex = -1
  public disableFindDataOnLoad = false
  public disableFindDataOnQueryChange = false
  public disableRefreshDataOnDataEdited = false
  // Reactive!
  public query: UnwrapNestedRefs<DataListQuery>
  // public dataList: UnwrapNestedRefs<DataListDataItems>
  public dataList: ShallowReactive<DataListDataItems>
  public queryChangeCallbackFunctions: { [key: string]: QueryChangeCallbackFunction } = {}
  public lastQueryChangedAt: Ref = ref(0)
  public lastDataFetchedAt: Ref = ref(0)
  public FilterControlsServiceInstance: FilterControlsService
  private _queryServiceStringified = ''
  private _findCancellablePromise: CancellablePromise<any> | null = null
  public wrapperVueComponentReferenceFunction: () => any // VueComponent
  constructor({
    wrapperVueComponentReferenceFunction,
  }: {
    wrapperVueComponentReferenceFunction?: () => any // VueComponent
  } = {}) {
    super()
    this.wrapperVueComponentReferenceFunction = wrapperVueComponentReferenceFunction
    // reactive: query 変更を検知する with callbacks
    this.query = reactive<DataListQuery>(new DataListQuery(this))
    this.FilterControlsServiceInstance = new FilterControlsService({
      ComposableDataListServiceInstance: this,
      ModelFormServiceInstance: null,
    })
    // shallowReactive: 取得したItemをreactiveにしないでおく
    this.dataList = shallowReactive(new DataListDataItems(this))
    this.initQueryChangedCallback()
    this.initDataEditedChangedCallback()
  }

  get wrapperVueComponentInstance(): any | null {
    return this.wrapperVueComponentReferenceFunction?.() || null
  }

  // 正しい状態かどうか
  get isValidState(): boolean {
    return !!this.model && !!this.columns
  }

  initQueryChangedCallback() {
    this.queryChangeCallbackFunctions['findDataOnQueryChange'] = (ins) => {
      if (!this.disableFindDataOnQueryChange) {
        setTimeout(() => {
          this.find()
        }, 1)
      }
    }

    // this.query の状態変更を監視 (deep)
    setTimeout(() => {
      watch(
        this.query,
        (value, oldValue, onCleanup) => {
          // ここで、query が変化したかどうかを判断, 変化していなければ、find() を実行しない
          const _stringified = JSON.stringify(value.findQueryParam)
          if (
            this._queryServiceStringified &&
            _stringified === this._queryServiceStringified
          ) {
            return // Query が変化していないので skip
          }
          this._queryServiceStringified = _stringified
          this.lastQueryChangedAt.value = new Date().getTime()
          this.invokeQueryChangeCallbackFunctions()
        },
        { deep: true },
      )
    }, 10)
  }

  initDataEditedChangedCallback() {
    // TODO: Watch の仕方: 本来、 $core.$uiState.userLatestStoreMethodActionTime だけをwatchしておきたいが...
    let lastUserLatestStoreMethodActionTime = 0
    let timer = null
    watch(
      $core.$uiState,
      (value, oldValue, onCleanup) => {
        if (
          this.disableRefreshDataOnDataEdited ||
          !value.userLatestStoreMethodActionTime ||
          lastUserLatestStoreMethodActionTime === value.userLatestStoreMethodActionTime
        ) {
          return // do nothing
        }
        lastUserLatestStoreMethodActionTime = value.userLatestStoreMethodActionTime
        if (timer) {
          clearTimeout(timer)
        }
        timer = setTimeout(() => {
          this.find()
          // Frameずらし
        }, 20)
      },
      { deep: true },
    )
  }

  get filteredColumns(): ColumnDefByColName {
    return this.virtualModel?.columnsMergedWithBaseModel || this.model.columns
  }

  get items() {
    return this.dataList.list
  }

  invokeQueryChangeCallbackFunctions() {
    Object.values(this.queryChangeCallbackFunctions).forEach((f) => f(this))
  }

  /**
   * データ fetch して良い状態かどうか？
   */
  get isFindable(): boolean {
    return !!this.isValidState
  }

  get isLoading() {
    return this._isLoading.value
  }

  set isLoading(value) {
    this._isLoading.value = value
  }

  /**
   * 指定された条件で検索を実行する
   */
  async find() {
    if (!this.isFindable) {
      return
    }
    // すでに実行中の場合、キャンセルする
    if (this._findCancellablePromise) {
      this._findCancellablePromise.cancel()
    }
    this.isLoading = true
    try {
      this._queryServiceStringified = JSON.stringify(this.query.findQueryParam)
      this._findCancellablePromise = new CancellablePromise(
        async (resolve, reject, _cancellablePromiseInstance) => {
          const res = await this.model.findWithCount(this.query.findQueryParam)
          resolve(res)
        },
      )
      const res = await this._findCancellablePromise
      // ページングを掛けている状態で、データが0件になってしまう場合、ページを1にリセットする
      if (this.query?.page !== 1 && !res.data?.length && res.meta.filter_count) {
        this.query.page = 1
      } else {
        this.dataList.list = res.data
        this.dataList.filter_count = res.meta.filter_count
      }
      this.updateLastDataFetchedAt()
    } catch (e) {
      if (e instanceof CanceledError) {
        // console.log(`[ComposableDataListService] Promise was canceled, with query: ${this._queryServiceStringified}`)
        return
      }
      console.error(e)
    } finally {
      this.isLoading = false
    }
  }
  cancelFind() {
    if (this._findCancellablePromise) {
      this._findCancellablePromise.cancel()
    }
  }

  updateLastDataFetchedAt() {
    // check if this.lastDataFetchedAt is Vue's ref instance
    if (this.lastDataFetchedAt.value) {
      this.lastDataFetchedAt.value = new Date().getTime()
    } else {
      this.lastDataFetchedAt = ref(new Date().getTime())
    }
    // $appHook event を 発火
    try {
      setTimeout(() => {
        $core.$appHook.emit('ComposableDataListService.find.after', {
          ComposableDataListServiceInstance: this,
          dataList: this.dataList,
          query: this.query,
        })
      }, 10)
    } catch (e) {
      console.error(e)
    }
  }

  exportAsExcel() {
    return this.dataList.exportAsExcel()
  }
}

/**
 * ComposableDataListService に関連
 * DataList の 検索条件を管理するクラス
 */
export class DataListDataItems {
  _list: Record<string, any>[] = []
  listLastUpdated = 0
  /**
   * 現在の検索条件内での total count
   */
  public filter_count = 0

  constructor(public readonly $dls: ComposableDataListService) {}

  get itemLength(): number {
    return this.list?.length || 0
  }

  get pageCount(): number {
    return Math.ceil((this.filter_count || 0) / this.$dls.query.limit)
  }

  get list(): Record<string, any>[] {
    return this._list
  }

  set list(v: Record<string, any>[]) {
    this._list = v
    this.listLastUpdated = Date.now()
  }

  exportAsExcel() {
    $core.$exportAsExcel.fromArrayOfObject({
      data: this.list,
      filename: `${this.$dls.modelName}_export_${$core.$dayjs().format('YYYYMMDDHHmmss')}`,
      modelName: this.$dls.modelName,
      sheetName: this.$dls.modelName,
    })
  }
}
